import { FC, FormEvent, useEffect, useState } from "react";
import {
  createResource,
  deleteResource,
  listResource,
  updateResource,
} from "data/adminApi";
import ConfirmDialog from "components/Admin/ConfirmDialog";
import FileUploader from "components/Admin/FileUploader";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";

interface Category {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  subcategory_count?: number;
}

interface SubCategory {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  category: number;
  category_name?: string;
  product_count?: number;
}

const emptyCat = { name: "", description: "", image_url: "" };
const emptySub = {
  name: "",
  description: "",
  image_url: "",
  category: 0,
};

const AdminCategories: FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [catForm, setCatForm] = useState(emptyCat);
  const [subForm, setSubForm] = useState(emptySub);
  const [editCatId, setEditCatId] = useState<number | null>(null);
  const [editSubId, setEditSubId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "category" | "subcategory";
    id: number;
    name: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    const [cats, subcats] = await Promise.all([
      listResource<Category>("/categories/"),
      listResource<SubCategory>("/subcategories/"),
    ]);
    setCategories(cats);
    setSubs(subcats);
  };

  useEffect(() => {
    load().catch(() => setMessage("Failed to load categories."));
  }, []);

  const saveCategory = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      if (editCatId) {
        await updateResource(`/categories/${editCatId}/`, catForm);
      } else {
        await createResource("/categories/", catForm);
      }
      setCatForm(emptyCat);
      setEditCatId(null);
      await load();
      setMessage("Category saved.");
    } catch (err: any) {
      setMessage(err?.response?.data?.detail || "Could not save category.");
    } finally {
      setLoading(false);
    }
  };

  const saveSub = async (e: FormEvent) => {
    e.preventDefault();
    if (!subForm.category) {
      setMessage("Select a parent category.");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      if (editSubId) {
        await updateResource(`/subcategories/${editSubId}/`, subForm);
      } else {
        await createResource("/subcategories/", subForm);
      }
      setSubForm(emptySub);
      setEditSubId(null);
      await load();
      setMessage("Subcategory saved.");
    } catch {
      setMessage("Could not save subcategory.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      const path =
        deleteTarget.type === "category"
          ? `/categories/${deleteTarget.id}/`
          : `/subcategories/${deleteTarget.id}/`;
      await deleteResource(path);
      await load();
      setMessage("Deleted successfully.");
    } catch {
      setMessage("Delete failed. Item may have related records.");
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
          Categories
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Manage product categories and subcategories.
        </p>
      </div>

      {message && (
        <div className="rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200 text-sm px-4 py-3">
          {message}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <form
          onSubmit={saveCategory}
          className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 space-y-4"
        >
          <h2 className="font-bold">
            {editCatId ? "Edit category" : "Add category"}
          </h2>
          <input
            required
            placeholder="Name"
            value={catForm.name}
            onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <input
            placeholder="Description"
            value={catForm.description}
            onChange={(e) =>
              setCatForm({ ...catForm, description: e.target.value })
            }
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <input
            placeholder="Image URL"
            value={catForm.image_url}
            onChange={(e) =>
              setCatForm({ ...catForm, image_url: e.target.value })
            }
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <FileUploader
            folder="categories"
            label="Upload category image"
            onUploaded={(url) => setCatForm({ ...catForm, image_url: url })}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 rounded-xl bcr-btn-primary text-sm font-semibold"
            >
              {editCatId ? "Update" : "Create"}
            </button>
            {editCatId && (
              <button
                type="button"
                onClick={() => {
                  setEditCatId(null);
                  setCatForm(emptyCat);
                }}
                className="h-10 px-4 rounded-xl border text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <form
          onSubmit={saveSub}
          className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 space-y-4"
        >
          <h2 className="font-bold">
            {editSubId ? "Edit subcategory" : "Add subcategory"}
          </h2>
          <select
            required
            value={subForm.category || ""}
            onChange={(e) =>
              setSubForm({ ...subForm, category: Number(e.target.value) })
            }
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          >
            <option value="">Parent category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            required
            placeholder="Name"
            value={subForm.name}
            onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <input
            placeholder="Description"
            value={subForm.description}
            onChange={(e) =>
              setSubForm({ ...subForm, description: e.target.value })
            }
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <input
            placeholder="Image URL"
            value={subForm.image_url}
            onChange={(e) =>
              setSubForm({ ...subForm, image_url: e.target.value })
            }
            className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <FileUploader
            folder="categories"
            label="Upload subcategory image"
            onUploaded={(url) => setSubForm({ ...subForm, image_url: url })}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 rounded-xl bcr-btn-primary text-sm font-semibold"
            >
              {editSubId ? "Update" : "Create"}
            </button>
            {editSubId && (
              <button
                type="button"
                onClick={() => {
                  setEditSubId(null);
                  setSubForm(emptySub);
                }}
                className="h-10 px-4 rounded-xl border text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
          <div className="px-5 py-3 border-b font-bold">Categories</div>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {categories.map((c) => (
              <li
                key={c.id}
                className="px-5 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{c.name}</p>
                  <p className="text-xs text-neutral-500">
                    {c.subcategory_count || 0} subcategories
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditCatId(c.id);
                      setCatForm({
                        name: c.name,
                        description: c.description || "",
                        image_url: c.image_url || "",
                      });
                    }}
                    className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDeleteTarget({
                        type: "category",
                        id: c.id,
                        name: c.name,
                      })
                    }
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
          <div className="px-5 py-3 border-b font-bold">Subcategories</div>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {subs.map((s) => (
              <li
                key={s.id}
                className="px-5 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{s.name}</p>
                  <p className="text-xs text-neutral-500">
                    {s.category_name} · {s.product_count || 0} products
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditSubId(s.id);
                      setSubForm({
                        name: s.name,
                        description: s.description || "",
                        image_url: s.image_url || "",
                        category: s.category,
                      });
                    }}
                    className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDeleteTarget({
                        type: "subcategory",
                        id: s.id,
                        name: s.name,
                      })
                    }
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete item?"
        message={`This will permanently delete "${deleteTarget?.name}". Related products may block this action.`}
        loading={loading}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminCategories;
